import axios from "axios";
import { parse, stringify } from "qs";
import format from "date-fns/format";
import clone from "lodash/clone";
import isEmpty from "lodash/isEmpty";
import moment from "moment";

/**
 * Use this to transform your payload before passing it to the backend.
 * It will parse the payload transform:
 *  - empty string, array, object, null to undefined
 *  - Date object to formatted string
 * @param originalPayload
 * @returns {string|*}
 */
export function transform(originalPayload) {
  let payload = clone(originalPayload);
  if (payload instanceof Date) {
    return format(payload, "yyyy-MM-dd");
  } else if (payload === null || payload === "" || (payload instanceof Object && isEmpty(payload))) {
    return undefined;
  } else if (payload instanceof Object) {
    Object.keys(payload).forEach((key) => {
      payload[key] = transform(payload[key]);
    });
    return payload;
  }
  return payload;
}

/**
 * Use this to transform the backend payload before passing it to the frontend.
 * It will parse the payload and reverse transform:
 *  - string formatted as "YYYY-MM-DD" to Date object
 * @param originalPayload
 * @returns {string|*}
 */

export function reverse(originalPayload) {
  let payload = clone(originalPayload);
  if (typeof payload === "string") {
    const possiblyDate = moment(payload, "YYYY-MM-DD", true);
    if (possiblyDate.isValid()) {
      return possiblyDate.toDate();
    }
  } else if (payload instanceof Object) {
    Object.keys(payload).forEach((key) => {
      payload[key] = reverse(payload[key]);
    });
    return payload;
  }
  return payload;
}

export class BackEndError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

const baseURL = window._env_.REACT_APP_POI_URL;

const ACCESS_TOKEN_NO_LONGER_VALID_ERROR_CODE = "F300";

class ApiService {
  tokenHandler = null;

  constructor(tokenHandler) {
    this.tokenHandler = tokenHandler;
    this.api = axios.create({
      baseURL,
      paramsSerializer: {
        encode: parse,
        serialize: stringify,
      },
      headers: {
        "Content-Type": "application/json",
        "x-api-key": window._env_.REACT_APP_X_API_KEY,
      },
    });
  }

  handleError = async (error, retryCount, retryFunction) => {
    console.error({ error });

    const shouldRetry =
      retryCount > 0 &&
      this.tokenHandler &&
      retryFunction &&
      error?.response?.data?.data?.[0]?.code === ACCESS_TOKEN_NO_LONGER_VALID_ERROR_CODE;
    if (shouldRetry) {
      const tokenResponse = await this.tokenHandler.renew("accessToken");
      this.api.defaults.headers.common.Authorization = `Bearer ${tokenResponse.accessToken}`;
      axios.defaults.headers.common.Authorization = `Bearer ${tokenResponse.accessToken}`;
      return retryFunction();
    }

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      switch (error.response.status) {
        case 401:
          throw new Error("Unauthorized");
        case 404:
          throw new Error("Not Found");
        case 400:
          const errorData = error.response.data.data;
          if (errorData) {
            if (Array.isArray(errorData)) {
              if (errorData[0].message) {
                throw new BackEndError(errorData[0].message, errorData[0].code);
              }
            }
          }
          // Fallback to default error in case of the 400 is not correctly structured on the backend side
          throw new Error(errorData.message || error.response.data.message);
        default:
          throw new BackEndError(
            `Got a ${error.response.status} error with no message from the server`,
            "CRITICAL_ERROR"
          );
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      throw new BackEndError("No response received", "ERROR_NO_RESPONSE");
    } else {
      // Something happened in setting up the request that triggered an Error
      throw new BackEndError(error.message, "CRITICAL_ERROR");
    }
  };

  shouldRetry = () => { };

  async get(url, config, retryCount = 1) {
    try {
      const response = await this.api.get(url, config);
      return reverse(response.data);
    } catch (error) {
      return this.handleError(error, retryCount, () => this.get(url, config, retryCount - 1));
    }
  }

  async post(url, data, config) {
    try {
      const response = await this.api.post(url, transform(data), config);
      return response.data;
    } catch (error) {
      this.handleError(error, 0);
    }
  }

  async getBooleanCheck(params = {}) {
    delete params.terms;
    delete params.info;

    if (params.vin === "") delete params.vin;
    if (params.licensePlate === "") delete params.licensePlate;

    const data = await this.get("/boolean-check", { params });

    const result = {
      status: data.data.status,
      makeName: data.data.makeName,
      modelName: data.data.modelName,
      plate: params.licensePlate || "",
      VIN: params.vin || "",
      date: params.date || "",
    };

    return result;
  }
}
const apiService = new ApiService();

export default apiService;
